import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { noop } from "ts-essentials";
import type { AndOrCondition, Condition } from "src/types/visual";

export type AgentNudgeMetadata = {
  focusedConditions?: AndOrCondition<Condition>;
  focusedLabel?: string;
  autoSubmit?: boolean;
};

type NudgeListener = (prompt: string, metadata?: AgentNudgeMetadata) => void;
type LoadSessionListener = (sessionId: string) => void;
type OpenListener = () => void;

type AgentControlContextType = {
  agentAvailable: boolean;
  /** Send a prompt to the agent pane (opens the panel if closed). */
  nudgeAgent?: (prompt: string, metadata?: AgentNudgeMetadata) => void;
  /** Open the agent panel and load a session into it. */
  loadSession?: (sessionId: string) => void;
  /** All of these return an unregister callback, designed to be returned from useEffect. */
  registerNudgeListener: (listener: NudgeListener) => () => void;
  registerLoadSessionListener: (listener: LoadSessionListener) => () => void;
  registerOpenListener: (listener: OpenListener) => () => void;
};

const AgentControlContext = createContext<AgentControlContextType>({
  agentAvailable: false,
  registerNudgeListener: () => noop,
  registerLoadSessionListener: () => noop,
  registerOpenListener: () => noop,
});
AgentControlContext.displayName = "AgentControlContext";

export const useAgentControlContext = () => useContext(AgentControlContext);

export const AgentControlProvider: FC<
  PropsWithChildren<{ agentAvailable: boolean }>
> = ({ agentAvailable, children }) => {
  const nudgeListeners = useRef(new Set<NudgeListener>());
  const loadSessionListeners = useRef(new Set<LoadSessionListener>());
  const openListeners = useRef(new Set<OpenListener>());

  const fireOpenListeners = useCallback(() => {
    for (const listener of openListeners.current) {
      listener();
    }
  }, []);

  const nudgeAgent = useCallback(
    (prompt: string, metadata?: AgentNudgeMetadata) => {
      fireOpenListeners();
      for (const listener of nudgeListeners.current) {
        listener(prompt, metadata);
      }
    },
    [fireOpenListeners],
  );

  const loadSession = useCallback(
    (sessionId: string) => {
      fireOpenListeners();
      for (const listener of loadSessionListeners.current) {
        listener(sessionId);
      }
    },
    [fireOpenListeners],
  );

  const registerNudgeListener = useCallback((listener: NudgeListener) => {
    nudgeListeners.current.add(listener);
    return () => nudgeListeners.current.delete(listener);
  }, []);

  const registerLoadSessionListener = useCallback(
    (listener: LoadSessionListener) => {
      loadSessionListeners.current.add(listener);
      return () => loadSessionListeners.current.delete(listener);
    },
    [],
  );

  const registerOpenListener = useCallback((listener: OpenListener) => {
    openListeners.current.add(listener);
    return () => openListeners.current.delete(listener);
  }, []);

  const value = useMemo(
    () => ({
      agentAvailable,
      nudgeAgent,
      loadSession,
      registerNudgeListener,
      registerLoadSessionListener,
      registerOpenListener,
    }),
    [
      agentAvailable,
      nudgeAgent,
      loadSession,
      registerNudgeListener,
      registerLoadSessionListener,
      registerOpenListener,
    ],
  );

  return (
    <AgentControlContext.Provider value={value}>
      {children}
    </AgentControlContext.Provider>
  );
};
